(() => {
  // Move cookie consent to <body> so it escapes the footer group stacking context
  const cookieEl = document.querySelector('[data-dp-cookie-consent]');
  if (cookieEl) document.body.appendChild(cookieEl);

  const footers = document.querySelectorAll('[data-dp-footer]');
  if (!footers.length) return;

  footers.forEach((footer) => {
    const panel = footer.querySelector('[data-dp-email-capture]');
    const openButton = footer.querySelector('[data-dp-email-open]');
    const closeButton = footer.querySelector('[data-dp-email-close]');
    const cookieBanner = document.querySelector('[data-dp-cookie-consent]');
    const cookieAcceptButton = cookieBanner?.querySelector('[data-dp-cookie-accept]');
    const cookieRejectButton = cookieBanner?.querySelector('[data-dp-cookie-reject]');
    const hasCookieBanner = Boolean(cookieBanner);

    const storageKey = 'dpCookieConsent';
    let customerPrivacyReady = Promise.resolve(null);


    const loadCustomerPrivacy = () => {
      if (!window.Shopify?.loadFeatures) return Promise.resolve(null);

      return new Promise((resolve) => {
        window.Shopify.loadFeatures(
          [
            {
              name: 'consent-tracking-api',
              version: '0.1',
            },
          ],
          (error) => {
            resolve(error ? null : window.Shopify?.customerPrivacy);
          },
        );
      });
    };

    customerPrivacyReady = loadCustomerPrivacy();

    const getCookieConsent = () => {
      try {
        return window.localStorage.getItem(storageKey);
      } catch (error) {
        return null;
      }
    };

    const setCookieConsent = (value) => {
      try {
        window.localStorage.setItem(storageKey, value);
      } catch (error) {
        // If storage is unavailable, the banner still dismisses for this page view.
      }

      document.dispatchEvent(new CustomEvent('dp:cookie-consent', { detail: { value } }));
    };

    const updateGoogleConsentMode = (accepted) => {
      window.dataLayer = window.dataLayer || [];

      const consentState = {
        ad_storage: accepted ? 'granted' : 'denied',
        analytics_storage: accepted ? 'granted' : 'denied',
        ad_user_data: accepted ? 'granted' : 'denied',
        ad_personalization: accepted ? 'granted' : 'denied',
      };

      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', consentState);
      } else {
        window.dataLayer.push(['consent', 'update', consentState]);
      }
    };

    const setShopifyConsent = async (accepted) => {
      const customerPrivacy = await customerPrivacyReady;
      if (!customerPrivacy?.setTrackingConsent) return;

      return new Promise((resolve) => {
        customerPrivacy.setTrackingConsent(
          {
            analytics: accepted,
            marketing: accepted,
            preferences: accepted,
          },
          resolve,
        );
      });
    };

    if (cookieBanner && cookieAcceptButton && cookieRejectButton) {
      const setCookieBannerVisible = (isVisible) => {
        cookieBanner.classList.toggle('is-hidden', !isVisible);
        document.body.classList.toggle('dp-cookie-consent-visible', isVisible);
      };

      customerPrivacyReady.then((customerPrivacy) => {
        const localConsent = getCookieConsent();
        const shouldShowByShopify = customerPrivacy?.shouldShowBanner?.();
        const visitorConsent = customerPrivacy?.currentVisitorConsent?.();
        const hasShopifyConsent =
          visitorConsent && ['analytics', 'marketing', 'preferences'].some((key) => visitorConsent[key]);

        if (!localConsent && (shouldShowByShopify || (!customerPrivacy && hasCookieBanner) || !hasShopifyConsent)) {
          document.body.classList.add('dp-no-transitions');
          setCookieBannerVisible(true);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            document.body.classList.remove('dp-no-transitions');
          }));
        }
      });

      const dismissCookieBanner = async (value) => {
        const accepted = value === 'accepted';
        await setShopifyConsent(accepted);
        updateGoogleConsentMode(accepted);
        setCookieConsent(value);
        setCookieBannerVisible(false);
      };

      cookieAcceptButton.addEventListener('click', () => dismissCookieBanner('accepted'));
      cookieRejectButton.addEventListener('click', () => dismissCookieBanner('rejected'));
    }

    if (!panel || !openButton || !closeButton) return;

    const newsletterForm = panel.querySelector('.dp-email-capture__form');
    const setNewsletterMessage = (message, role = 'status') => {
      const messageElement = document.createElement('p');
      messageElement.className = 'dp-email-capture__message dp-email-capture__message--inline';
      messageElement.setAttribute('role', role);
      messageElement.tabIndex = -1;
      messageElement.textContent = message;

      newsletterForm.replaceChildren(messageElement);
      messageElement.focus();
    };

    if (newsletterForm) {
      const iframeName = `${newsletterForm.id || 'DpEmailCaptureForm'}Frame`;
      const iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.hidden = true;
      iframe.tabIndex = -1;
      iframe.title = '';
      newsletterForm.after(iframe);
      newsletterForm.target = iframeName;

      newsletterForm.addEventListener('submit', () => {
        newsletterForm.querySelectorAll('.dp-email-capture__message--inline').forEach((element) => element.remove());
        window.setTimeout(() => {
          setNewsletterMessage('Thanks for subscribing.');
        }, 100);
      });
    }

    // manageFocus is skipped for automatic state changes (initial, viewport sync,
    // delayed reveal) so the page doesn't scroll or steal focus without user intent.
    const setOpen = (isOpen, manageFocus = true) => {
      panel.classList.toggle('is-hidden', !isOpen);
      panel.setAttribute('aria-hidden', String(!isOpen));
      openButton.setAttribute('aria-expanded', String(isOpen));

      if (!manageFocus) return;

      if (isOpen) {
        window.setTimeout(() => {
          panel.querySelector('input, .dp-email-capture__close')?.focus();
        }, 120);
      } else {
        openButton.focus();
      }
    };

    if (!panel.id) panel.id = 'DpEmailCapture';
    openButton.setAttribute('aria-controls', panel.id);

    const delayMs = Math.max(0, (parseFloat(footer.getAttribute('data-dp-email-delay')) || 0) * 1000);
    const mobileQuery = window.matchMedia('(max-width: 989px)');
    const dismissedKey = 'dpEmailCaptureDismissed';
    let revealTimer;

    const isDismissed = () => { try { return sessionStorage.getItem(dismissedKey) === '1'; } catch { return false; } };
    const setDismissed = () => { try { sessionStorage.setItem(dismissedKey, '1'); } catch {} };

    const syncForViewport = () => {
      window.clearTimeout(revealTimer);
      if (mobileQuery.matches || isDismissed()) {
        setOpen(false, false);
      } else if (delayMs > 0) {
        setOpen(false, false);
        revealTimer = window.setTimeout(() => {
          if (!mobileQuery.matches && !isDismissed()) setOpen(true, false);
        }, delayMs);
      } else {
        setOpen(true, false);
      }
    };

    openButton.addEventListener('click', () => {
      window.clearTimeout(revealTimer);
      setOpen(panel.classList.contains('is-hidden'));
    });
    closeButton.addEventListener('click', () => {
      window.clearTimeout(revealTimer);
      setDismissed();
      setOpen(false);
    });
    mobileQuery.addEventListener('change', syncForViewport);
    syncForViewport();

    footer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.classList.contains('is-hidden')) {
        window.clearTimeout(revealTimer);
        setOpen(false);
      }
    });
  });
})();
